import "strophe.js";
import "strophejs-plugin-roster";
import { useApp } from "@/store/app";
import { commEmitter } from "../events/emitter";
import environment from "@/modules/environment";
import { decodeBase64, decodeEnvelope, getEnvelopeContent } from "../communication";
import MultiPartAssembler from "../communication/multi-part-assembler";
import XmppUtils, { DISCONNECT_TIMEOUT, getChildText, getFullJid } from "./XmppUtils";
import PresenceKeeper from "../app/Presence";
import PingService from "./PingService";
import Roster from "./Roster";
import { isEmptyObject } from "@/utils";

export default class Xmpp extends XmppUtils {
  private static instance: Xmpp;
  private assembler = new MultiPartAssembler();
  static getInstance() {
    if (!Xmpp.instance) {
      Xmpp.instance = new Xmpp();
      log.xmpp("Xmpp instance created");
    }
    return Xmpp.instance;
  }
  private credentials!: XmppCredentials;
  private connection!: Strophe.Connection;
  presence: Presence = "not_chosen_yet";
  payload: Keyable = {};
  pingService = new PingService(() => this.connection);
  private roster = new Roster(
    () => this.connection,
    () => this.sendPresence()
  );
  private messageHandler: StropheHandler = null;
  private iqHandler: StropheHandler = null;
  private presenceHandler: StropheHandler = null;
  private rosterHandler: StropheHandler = null;
  private get appStatus() {
    return useApp.getState().status;
  }

  setCredentials = (credentials: XmppCredentials) => {
    log.xmpp("Setting xmpp credentials", credentials);
    this.credentials = {
      xmppLogin: getFullJid(credentials.xmppLogin),
      xmppPassword: credentials.xmppPassword
    };
  };

  private onStatus = (status: Strophe.Status) => {
    this.status = status;

    if (status === Strophe.Status.CONNECTING) {
      log.xmpp("Strophe is connecting.");
    } else if (status === Strophe.Status.CONNFAIL) {
      log.xmpp("Strophe failed to connect.");
      if (this.connectResolver) {
        this.connectResolver.reject("Connection resolver failed");
        this.connectResolver = null;
      }
    } else if (status === Strophe.Status.DISCONNECTING) {
      log.xmpp("Strophe is disconnecting.");
    } else if (status === Strophe.Status.DISCONNECTED) {
      log.xmpp("Strophe is disconnected.");
      if (this.disconnectResolver) {
        this.disconnectResolver.resolve(1);
        this.disconnectResolver = null;
      }
      if (
        this.appStatus !== "STARTING" &&
        this.appStatus !== "FAILURE" &&
        this.appStatus !== "XMPP_DISCONNECTED" &&
        this.appStatus !== "NO_NETWORK" &&
        this.appStatus !== "SIGNING_OUT"
      ) {
        useApp.getState().setStatus("XMPP_DISCONNECTED");
      }
    } else if (status === Strophe.Status.CONNECTED) {
      this.roster.subscribe(this.credentials.xmppLogin);
      log.xmpp("Strophe is connected.");
      if (this.connectResolver) {
        this.connectResolver.resolve(1);
        this.connectResolver = null;
      }
    }
  };

  private onMessage = (message: Element) => {
    if (this.isSelf(message)) return true;
    if (getChildText(message, "subject") !== "pb") return true;
    log.xmpp("received message", message);
    const base64EncodedEnvelope = getChildText(message, "body");

    const xmppLogin = message.getAttribute("from");
    if (!base64EncodedEnvelope) {
      log.xmpp("No envelope in message", message);
      return true;
    }
    const bufferEnvelope = decodeBase64(base64EncodedEnvelope);
    const decodedEnvelope = decodeEnvelope(bufferEnvelope);

    if (message.getAttribute("type") === "error") {
      log.xmpp("Received error message, ignoring", message);
      return true;
    }

    const jid = Strophe.getNodeFromJid(xmppLogin as string);

    if (decodedEnvelope.multipart) {
      const buffer = this.assembler.add(jid, decodedEnvelope.multipart);
      if (buffer == null) return true;
      const envelope = decodeEnvelope(buffer);
      const { type, payload } = getEnvelopeContent(envelope);
      commEmitter.emit(type, payload, jid);
      return true;
    }

    const { type, payload } = getEnvelopeContent(decodedEnvelope);
    commEmitter.emit(type, payload, jid);
    return true;
  };

  private onIq = (iq: Element) => {
    if (this.isSelf(iq)) return true;
    if (iq.querySelector("ping")) return true;
    log.xmpp("received iq", iq);
    return true;
  };

  private onPresence = (presence: Element) => {
    if (this.isSelf(presence)) return true;
    const xmppLogin = presence.getAttribute("from");
    const type = presence.getAttribute("type");
    let status = getChildText(presence, "status") as Presence | undefined;
    const payload = getChildText(presence, "payload");

    if (type === "unavailable" || status == null) {
      status = "unavailable";
    }
    log.xmpp("received presence", presence);
    const presenceUpdate = {
      jid: Strophe.getNodeFromJid(xmppLogin as string),
      status,
      payload: payload ? JSON.parse(payload) : null
    };
    log.xmpp(presenceUpdate);

    PresenceKeeper.update({
      jid: presenceUpdate.jid,
      status: presenceUpdate.status
    });

    if (presenceUpdate.payload && !isEmptyObject(presenceUpdate.payload)) {
      commEmitter.emit("presence-payload", presenceUpdate.payload, presenceUpdate.jid);
    }
    return true;
  };

  connect = () => {
    const promise = this.createConnectPromise();
    const connection = new Strophe.Connection(environment.xmppServer, { mechanisms: [Strophe.SASLPlain] });
    this.connection = connection;
    this.connection.connect(
      this.credentials.xmppLogin,
      this.credentials.xmppPassword,
      (status: Strophe.Status) => {
        const isCurrentConnection = connection === this.connection;
        if (isCurrentConnection) this.onStatus(status);
      },
      undefined,
      undefined,
      undefined,
      undefined,
      DISCONNECT_TIMEOUT
    );

    this.messageHandler = this.connection.addHandler(this.onMessage, "", "message");
    this.iqHandler = this.connection.addHandler(this.onIq, "", "iq");
    this.presenceHandler = this.connection.addHandler(this.onPresence, "", "presence");
    this.rosterHandler = this.connection.addHandler(this.roster.onRosterUpdate, Strophe.NS.ROSTER, "iq", "set");

    return promise;
  };

  sendPresence = (presence = this.presence, payload = this.payload) => {
    if (!this.isReady()) return;
    this.presence = presence;
    this.payload = payload;

    const { setPresence, presence: currentPresence } = useApp.getState();
    if (presence !== currentPresence) setPresence(presence);

    const presenceStanza = $pres()
      .c("status", {}, presence)
      .c("payload", { xmlns: "ttmonitor" }, JSON.stringify(payload))
      .c("priority", {}, "99");

    this.connection.sendPresence(
      presenceStanza,
      (e) => log.xmpp("Presence sent", e),
      (e) => log.xmpp("Error sending presence", e)
    );
  };

  sendEnvelope = (envelope: string, jid: string) => {
    if (!this.isReady()) return;
    const message = $msg({ to: getFullJid(jid), type: "chat" })
      .c("subject", {}, "pb")
      .c("body", {}, envelope);
    log.xmpp("Sending envelope", message);
    this.connection.send(message);
  };

  sendPushNotification = (pushNotification: string, jid: string) => {
    if (!this.isReady()) return;
    const message = $msg({ to: getFullJid(jid), type: "chat" })
      .c("subject", {}, "pushnotification")
      .c("body", {}, pushNotification);
    log.xmpp("Sending push notification", message);
    this.connection.send(message);
  };

  sendIq = (iq: Strophe.Builder) => {
    if (!this.isReady()) return;
    this.connection.sendIQ(iq);
  };

  disconnect = () => {
    try {
      const promise = this.createDisconnectPromise();
      this.pingService.stop();
      if (this.isReady()) this.roster.unsubscribe(this.credentials.xmppLogin);
      if (this.messageHandler) this.connection.deleteHandler(this.messageHandler);
      if (this.iqHandler) this.connection.deleteHandler(this.iqHandler);
      if (this.presenceHandler) this.connection.deleteHandler(this.presenceHandler);
      if (this.rosterHandler) this.connection.deleteHandler(this.rosterHandler);
      this.connection.disconnect("Disconnected called");

      this.messageHandler = null;
      this.iqHandler = null;
      this.presenceHandler = null;
      this.rosterHandler = null;

      return promise;
    } catch (err) {
      this.clearDisconnectTimeout();
      this.disconnectResolver = null;
      log.xmpp("Error disconnecting from xmpp server", err);
    }
  };

  reconnect = async () => {
    await this.disconnect();
    log.xmpp("Sucessfully disconnected while trying to reconnect");
    await this.connect();
    log.xmpp("Sucessfully connected while trying to reconnect");
    await this.getRoster();
    this.pingService.start();
    this.sendPresence(this.presence, this.payload);
  };

  isConnected = () => this.status === Strophe.Status.CONNECTED;

  getRoster = async (): Promise<RosterDevice[]> => {
    return new Promise((resolve, reject) => {
      if (!this.isReady()) {
        reject("'getRoster' failed, connection is not ready");
        return;
      }
      this.roster.getRoster(resolve);
    });
  };

  private isSelf = (stanza: Element): boolean => {
    const from = stanza.getAttribute("from");
    if (!from) return true;
    if (from === this.credentials.xmppLogin) return true;
    return false;
  };
}
